import { ELEMENTARY_CHARGE } from "@/lib/tool-kit/physics-waves/electromagnetic";
import { SI_PREFIXES, shiftExp } from "@/lib/tool-kit/physics-waves/format";

/** A unit a quantity can be typed in: `exp` is the power of ten relative to the SI base. */
export interface UnitDef {
  id: string;
  symbol: string;
  exp: number;
  /** Exact multiplier to the base unit for non-decimal units (km/h, lbf); default 1. */
  factor?: number;
}

/** `prefixUnits("V", ["m", "", "k"])` → mV, V, kV. The id spells µ as "u". */
export function prefixUnits(
  base: string,
  prefixes: readonly string[]
): UnitDef[] {
  const units: UnitDef[] = [];
  for (const symbol of prefixes) {
    const prefix = SI_PREFIXES.find((p) => p.symbol === symbol);
    if (prefix) {
      units.push({
        id: `${symbol === "µ" ? "u" : symbol}${base}`,
        symbol: `${symbol}${base}`,
        exp: prefix.exp,
      });
    }
  }
  return units;
}

/** What a typed number means in SI base units. */
export function toBase(value: number, unit: UnitDef): number {
  return shiftExp(value, unit.exp) * (unit.factor ?? 1);
}

/** An SI base value expressed in `unit`. */
export function fromBase(value: number, unit: UnitDef): number {
  return shiftExp(value / (unit.factor ?? 1), -unit.exp);
}

export function findUnit(units: readonly UnitDef[], id: string): UnitDef {
  return units.find((unit) => unit.id === id) ?? units[0];
}

/** Unit menus shared by the circuit calculators. */
export const VOLT_UNITS = prefixUnits("V", ["µ", "m", "", "k", "M"]);
export const AMP_UNITS = prefixUnits("A", ["µ", "m", "", "k"]);
export const OHM_UNITS = prefixUnits("Ω", ["m", "", "k", "M", "G"]);
export const WATT_UNITS = prefixUnits("W", ["µ", "m", "", "k", "M"]);
export const FARAD_UNITS = prefixUnits("F", ["p", "n", "µ", "m", ""]);
export const COULOMB_UNITS = prefixUnits("C", ["p", "n", "µ", "m", ""]);
export const JOULE_UNITS = prefixUnits("J", ["n", "µ", "m", "", "k"]);
export const SECOND_UNITS = prefixUnits("s", ["µ", "m", "", "k"]);

export const HERTZ_UNITS = prefixUnits("Hz", ["", "k", "M", "G", "T"]);
export const METER_UNITS = prefixUnits("m", ["n", "µ", "m", "", "k"]);
/** Speeds, in metres per second. */
export const SPEED_UNITS: UnitDef[] = [
  { id: "m/s", symbol: "m/s", exp: 0 },
  { id: "km/s", symbol: "km/s", exp: 3 },
  { id: "km/h", symbol: "km/h", exp: 0, factor: 1 / 3.6 },
  { id: "mph", symbol: "mph", exp: 0, factor: 0.447_04 },
  { id: "ft/s", symbol: "ft/s", exp: 0, factor: 0.3048 },
];
/** Speeds for relativistic work: km/s, m/s, and fractions of the speed of light. */
export const RELATIVISTIC_SPEED_UNITS: UnitDef[] = [
  { id: "km/s", symbol: "km/s", exp: 3 },
  { id: "m/s", symbol: "m/s", exp: 0 },
  { id: "c", symbol: "c", exp: 0, factor: 299_792_458 },
];
export const INTENSITY_UNITS = prefixUnits("W/m²", ["p", "n", "µ", "m", ""]);
export const PRESSURE_UNITS = prefixUnits("Pa", ["µ", "m", "", "k"]);
/** Distances for sound falloff. */
export const DISTANCE_UNITS: UnitDef[] = [
  { id: "cm", symbol: "cm", exp: -2 },
  { id: "m", symbol: "m", exp: 0 },
  { id: "km", symbol: "km", exp: 3 },
  { id: "ft", symbol: "ft", exp: 0, factor: 0.3048 },
];
/** A bare decibel value: the box is typed as a number, so the menu has one entry. */
export const DECIBEL_UNITS: UnitDef[] = [{ id: "dB", symbol: "dB", exp: 0 }];

const electronvolt = (symbol: string, exp: number): UnitDef => ({
  id: symbol,
  symbol,
  exp,
  factor: ELEMENTARY_CHARGE,
});

/** Energies: electronvolts with prefixes, and joules. */
export const ENERGY_UNITS: UnitDef[] = [
  electronvolt("µeV", -6),
  electronvolt("meV", -3),
  electronvolt("eV", 0),
  electronvolt("keV", 3),
  electronvolt("MeV", 6),
  electronvolt("GeV", 9),
  { id: "J", symbol: "J", exp: 0 },
];

/** How a photon can be described: by wavelength, frequency or energy. */
export const PHOTON_UNITS = {
  wavelength: [
    ...prefixUnits("m", ["p"]),
    { id: "A", symbol: "Å", exp: -10 },
    ...prefixUnits("m", ["n", "µ", "m", ""]),
  ] as UnitDef[],
  frequency: prefixUnits("Hz", ["", "k", "M", "G", "T", "P", "E"]),
  energy: ENERGY_UNITS,
};
